// src/components/bits/EditorialBit.jsx
import React from 'react';
import { motion } from 'framer-motion';
import LuxuryButton from "../ui/LuxuryButton";
import {Reveal} from "../animations/Reveal";
import demoVideo from "../../assets/images/demo_video.mp4";
import { Quote, PlayCircle } from 'lucide-react';

const EditorialBit = () => {
  return (
    <div className="relative">
      {/* Parallax Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-pearl via-nude/30 to-pearl" />
      
      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-px h-32 bg-gradient-to-b from-transparent via-champagne/20 to-transparent"
            style={{
              left: `${10 + i * 12}%`,
              top: '20%',
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: 4 + i,
              repeat: Infinity,
              delay: i * 0.5,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      <div className="container-luxury relative">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Quote Section */}
          <Reveal>
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 text-champagne">
                <Quote className="w-8 h-8" />
                <span className="text-sm tracking-widest uppercase">
                  Editorial
                </span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-light">
                Beauty as a <span className="italic text-sage">Language</span> of Self
              </h2>
              
              <div className="space-y-6 text-lg text-charcoal/70">
                <p>
                  In a world of noise, KROMAIC believes in the poetry of silence. 
                  Our editorial space is a sanctuary for conversations about 
                  mindful beauty, sustainable luxury, and the art of slow living.
                </p>
                
                <div className="pl-6 border-l-2 border-champagne/30">
                  <p className="italic">
                    "True luxury isn't about what you apply, but how you feel 
                    when you do. It's the pause between breaths, the intention 
                    in every gesture."
                  </p>
                  <p className="text-sm mt-4 text-charcoal/60">
                    — Anouk Laurent, Creative Director
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <LuxuryButton variant="primary">
                  Read Journal
                </LuxuryButton>
                <LuxuryButton
                  variant="ghost"
                  icon={<PlayCircle className="w-5 h-5" />}
                >
                  Watch Series
                </LuxuryButton>
              </div>
            </div>
          </Reveal>

          {/* Video/Image Section */}
          <Reveal delay={0.4}>
            <div className="relative">
              {/* Video Placeholder */}
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-black">
                <video
                    src={demoVideo}
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

                {/*/!* Play Button *!/*/}
                {/*<motion.button*/}
                {/*  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"*/}
                {/*  whileHover={{ scale: 1.1 }}*/}
                {/*  whileTap={{ scale: 0.95 }}*/}
                {/*  animate={{*/}
                {/*    boxShadow: [*/}
                {/*      '0 0 0 0 rgba(255, 255, 255, 0.2)',*/}
                {/*      '0 0 0 20px rgba(255, 255, 255, 0)',*/}
                {/*      '0 0 0 0 rgba(255, 255, 255, 0)'*/}
                {/*    ]*/}
                {/*  }}*/}
                {/*  transition={{*/}
                {/*    duration: 2,*/}
                {/*    repeat: Infinity,*/}
                {/*    ease: "easeOut"*/}
                {/*  }}*/}
                {/*>*/}
                {/*  <PlayCircle className="w-10 h-10 text-white" />*/}
                {/*</motion.button>*/}
                
                {/* Overlay Content */}
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                    <h3 className="text-xl font-medium text-white mb-2">
                      The Ritual Series
                    </h3>
                    <p className="text-white/80 text-sm">
                      A cinematic exploration of beauty rituals around the world
                    </p>
                    <div className="flex items-center gap-2 mt-4">
                      <div className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-white"
                          initial={{ width: "0%" }}
                          animate={{ width: "70%" }}
                          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                        />
                      </div>
                      <span className="text-white/60 text-sm">24:36</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative Elements */}
              <motion.div
                className="absolute -top-6 -right-6 w-32 h-32 border border-champagne/20 rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              />
              
              <motion.div
                className="absolute -bottom-6 -left-6 w-24 h-24 bg-sage/10 rounded-full"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default EditorialBit;